/**
 * Projects Service
 * Handles project management and project members
 */

import type { GatewayClient } from './client'
import type {
  Project,
  ProjectListResponse,
  CreateProjectRequest,
  UpdateProjectRequest,
  ProjectMembersResponse,
  InviteProjectMemberRequest,
  ProjectStorageInfo,
} from './types'

// ============================================================================
// Types
// ============================================================================

export interface ProjectsService {
  /**
   * List all projects for an organization
   */
  listProjects(orgId: string): Promise<ProjectListResponse>

  /**
   * Create a new project in an organization
   */
  createProject(
    orgId: string,
    name: string,
    description?: string
  ): Promise<Project>

  /**
   * Get a specific project by ID
   */
  getProject(projectId: string): Promise<Project>

  /**
   * Update a project's details
   */
  updateProject(
    projectId: string,
    name: string,
    description?: string
  ): Promise<Project>

  /**
   * Delete a project
   */
  deleteProject(projectId: string): Promise<void>

  /**
   * List members of a project
   */
  listProjectMembers(projectId: string): Promise<ProjectMembersResponse>

  /**
   * Invite a user to a project
   */
  inviteProjectMember(
    projectId: string,
    userId: string,
    role: string
  ): Promise<{ message: string }>

  /**
   * Remove a member from a project
   */
  removeProjectMember(projectId: string, userId: string): Promise<void>

  /**
   * Get storage information for a project
   */
  getProjectStorage(projectId: string): Promise<ProjectStorageInfo>
}

// ============================================================================
// Implementation
// ============================================================================

/**
 * Create a ProjectsService instance
 */
export function createProjectsService(client: GatewayClient): ProjectsService {
  return {
    async listProjects(orgId: string): Promise<ProjectListResponse> {
      return client.get<ProjectListResponse>(
        `/v1/organizations/${orgId}/projects`
      )
    },

    async createProject(
      orgId: string,
      name: string,
      description?: string
    ): Promise<Project> {
      const request: CreateProjectRequest = { name, description }
      return client.post<Project>(
        `/v1/organizations/${orgId}/projects`,
        request
      )
    },

    async getProject(projectId: string): Promise<Project> {
      return client.get<Project>(`/v1/projects/${projectId}`)
    },

    async updateProject(
      projectId: string,
      name: string,
      description?: string
    ): Promise<Project> {
      const request: UpdateProjectRequest = { name, description }
      return client.patch<Project>(`/v1/projects/${projectId}`, request)
    },

    async deleteProject(projectId: string): Promise<void> {
      await client.delete<void>(`/v1/projects/${projectId}`)
    },

    async listProjectMembers(
      projectId: string
    ): Promise<ProjectMembersResponse> {
      return client.get<ProjectMembersResponse>(
        `/v1/projects/${projectId}/members`
      )
    },

    async inviteProjectMember(
      projectId: string,
      userId: string,
      role: string
    ): Promise<{ message: string }> {
      const request: InviteProjectMemberRequest = { userId, role }
      return client.post<{ message: string }>(
        `/v1/projects/${projectId}/members/invite`,
        request
      )
    },

    async removeProjectMember(
      projectId: string,
      userId: string
    ): Promise<void> {
      await client.delete<void>(`/v1/projects/${projectId}/members/${userId}`)
    },

    async getProjectStorage(projectId: string): Promise<ProjectStorageInfo> {
      return client.get<ProjectStorageInfo>(`/v1/projects/${projectId}/storage`)
    },
  }
}
